// src/services/downloadService.js
import env from '../config/env';

// Convert a title to a URL-friendly slug
const createSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

// Get all downloads (metadata only)
export const getAllDownloads = async () => {
  try {
    const response = await fetch('/api/downloads');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error getting downloads:', error);
    throw error;
  }
};

// Get a single download by ID
export const getDownload = async (id) => {
  try {
    const response = await fetch(`/api/downloads/${id}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error getting download with ID ${id}:`, error);
    throw error;
  }
};

// Create a new download
export const createDownload = async (downloadData, file, thumbnail) => {
  try {
    const formData = new FormData();
    formData.append('data', JSON.stringify({
      ...downloadData,
      id: downloadData.id || createSlug(downloadData.title)
    }));
    
    if (file) {
      formData.append('file', file);
    }
    
    if (thumbnail) {
      formData.append('thumbnail', thumbnail);
    }
    
    const response = await fetch('/api/downloads', {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating download:', error);
    throw error;
  }
};

// Update an existing download
export const updateDownload = async (id, downloadData, file, thumbnail) => {
  try {
    const formData = new FormData();
    formData.append('data', JSON.stringify({
      ...downloadData,
      id
    }));
    
    if (file) {
      formData.append('file', file);
    }
    
    if (thumbnail) {
      formData.append('thumbnail', thumbnail);
    }
    
    const response = await fetch(`/api/downloads/${id}`, {
      method: 'PUT',
      body: formData
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error updating download with ID ${id}:`, error);
    throw error;
  }
};

// Delete a download
export const deleteDownload = async (id) => {
  try {
    const response = await fetch(`/api/downloads/${id}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error deleting download with ID ${id}:`, error);
    throw error;
  }
};

// Track download count
export const trackDownload = async (id) => {
  try {
    const response = await fetch(`/api/downloads/${id}/track`, {
      method: 'POST'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error tracking download for ID ${id}:`, error);
    throw error;
  }
};

// Helper function to format file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default {
  getAllDownloads,
  getDownload,
  createDownload,
  updateDownload,
  deleteDownload,
  trackDownload,
  formatFileSize
};
